'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Bot, Megaphone, Sparkles, Table2 } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const SECTIONS = [
  { href: '/ai-scans', label: 'Overview', icon: Sparkles, matches: (path: string) => path === '/ai-scans' },
  {
    href: '/ai-scans/screener',
    label: 'Screener',
    icon: Table2,
    matches: (path: string) => path.startsWith('/ai-scans/screener') || path.startsWith('/ai-scans/stocks'),
  },
  {
    href: '/ai-scans/announcements',
    label: 'Announcements',
    icon: Megaphone,
    matches: (path: string) => path.startsWith('/ai-scans/announcements'),
  },
  {
    href: '/ai-scans/claude',
    label: 'Use with Claude',
    icon: Bot,
    matches: (path: string) => path.startsWith('/ai-scans/claude'),
  },
];

export default function AIScansShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? '';

  return (
    <PageLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <nav
          aria-label="AI Scans sections"
          className="-mx-4 mb-6 overflow-x-auto px-4 sm:mx-0 sm:px-0 [scrollbar-width:none]"
        >
          <div className="inline-flex items-center gap-1 rounded-full border border-zinc-200/80 bg-white p-1 dark:border-white/[0.08] dark:bg-white/[0.04]">
            {SECTIONS.map(({ href, label, icon: Icon, matches }) => {
              const active = matches(pathname);
              return (
                <Link
                  key={href}
                  href={href}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>

        {children}

        <p className="mt-12 border-t border-zinc-200 pt-6 text-center text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-500">
          AI Scans data is for research and learning only. It is not investment advice — always do
          your own checks before you invest.
        </p>
      </div>
    </PageLayout>
  );
}
